import React, { useState } from "react";
import { useLang } from "@/contexts/LanguageContext";
import api from "@/lib/apiClient";
import { toast } from "sonner";
import useSeo from "@/hooks/useSeo";
import { MapPin, Phone, EnvelopeSimple, WhatsappLogo, InstagramLogo, FacebookLogo } from "@phosphor-icons/react";
import { CONTACT_EMAIL, MAILTO, ADDRESSES, WHATSAPP_URL, WHATSAPP_DISPLAY, SOCIAL } from "@/config/siteInfo";

export default function ContactPage() {
  useSeo({
    title: "Contact & Visit Us",
    description: `Visit VELOTEKA in Strumica or Deggendorf, or reach us at ${CONTACT_EMAIL}. Workshop, sales, and customer service — one team.`,
    path: "/contact",
  });
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent. We will reply soon.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch { toast.error("Please try again."); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16" data-testid="contact-page">
      <div className="mb-12">
        <div className="overline text-[color:var(--vt-orange)] mb-3">/ REACH US</div>
        <h1 className="font-display font-black text-6xl md:text-7xl tracking-tighter">{t.contact.title}</h1>
      </div>
      <div className="grid lg:grid-cols-2 gap-10">
        <div>
          <div className="space-y-4 mb-8">
            {ADDRESSES.map((a) => (
              <div key={a.label} className="flex items-start gap-3" data-testid={`contact-address-${a.label.toLowerCase()}`}>
                <MapPin size={22} weight="duotone" className="text-[color:var(--vt-orange)]" />
                <div>
                  <div className="font-bold">Showroom · {a.label}</div>
                  <div className="text-neutral-600 text-sm">{a.line1}</div>
                  <div className="text-neutral-600 text-sm">{a.line2}</div>
                </div>
              </div>
            ))}
            <div className="flex items-start gap-3">
              <Phone size={22} weight="duotone" className="text-[color:var(--vt-orange)]" />
              <div>
                <div className="font-bold">Phone / WhatsApp</div>
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="text-neutral-600 text-sm hover:text-black">{WHATSAPP_DISPLAY}</a>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <EnvelopeSimple size={22} weight="duotone" className="text-[color:var(--vt-orange)]" />
              <div>
                <div className="font-bold">Email</div>
                <a href={MAILTO} className="text-neutral-600 text-sm hover:text-black" data-testid="contact-email-link">{CONTACT_EMAIL}</a>
              </div>
            </div>
            <div className="flex gap-4 pt-2">
              <a href={SOCIAL.whatsapp} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" className="hover:text-[color:var(--vt-orange)]"><WhatsappLogo size={26} /></a>
              <a href={SOCIAL.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="hover:text-[color:var(--vt-orange)]"><InstagramLogo size={26} /></a>
              <a href={SOCIAL.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="hover:text-[color:var(--vt-orange)]"><FacebookLogo size={26} /></a>
            </div>
          </div>
          <div className="border border-neutral-200 aspect-[4/3] overflow-hidden">
            <iframe
              title="VELOTEKA Location"
              src={`https://www.google.com/maps?q=${encodeURIComponent(ADDRESSES[0].line1 + ', ' + ADDRESSES[0].line2)}&output=embed`}
              className="w-full h-full"
            />
          </div>
        </div>

        <form onSubmit={submit} className="border border-neutral-200 p-8 space-y-4" data-testid="contact-form">
          <h3 className="font-display font-bold text-2xl">{t.contact.form}</h3>
          <input required className="vt-input" placeholder="Name" value={form.name} onChange={upd("name")} data-testid="contact-name" />
          <input required type="email" className="vt-input" placeholder="Email" value={form.email} onChange={upd("email")} data-testid="contact-email" />
          <input className="vt-input" placeholder="Phone" value={form.phone} onChange={upd("phone")} />
          <input required className="vt-input" placeholder={t.contact.subject} value={form.subject} onChange={upd("subject")} data-testid="contact-subject" />
          <textarea required className="vt-input" rows="5" placeholder={t.contact.message} value={form.message} onChange={upd("message")} data-testid="contact-message" />
          <button disabled={submitting} className="vt-btn-orange w-full" data-testid="contact-submit">{submitting ? "..." : t.contact.send}</button>
        </form>
      </div>
    </div>
  );
}
